import React, { useState } from 'react';
import axios from 'axios';
import { ArrowLeftRight, AlertTriangle, Scissors, Loader2, Clock } from 'lucide-react';
import { toast } from 'sonner';
import { TransferPairConfirmDialog } from './TransferPairConfirmDialog';
import { getCurrencySymbol } from '../../utils/currency';

const txSymbol = (tx, fallback) => (tx?.currency ? getCurrencySymbol(tx.currency) : fallback);

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/** Chip on review-needed rows held because Stripe hasn't reported the payout yet. */
export const StripeHoldChip = ({ tx }) => {
  if (tx.status !== 'review_needed' || tx.review_reason_code !== 'stripe_payout_awaiting_details') return null;
  return (
    <span
      className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-semibold bg-violet-50 text-violet-700 border border-violet-300 leading-none"
      title={tx.review_reason || 'Stripe has not reported the matching payout yet. This row posts automatically (bank → Stripe clearing) once the payout details arrive.'}
      data-testid={`stripe-hold-${tx.id}`}
    >
      <Clock className="w-2.5 h-2.5" /> Awaiting Stripe payout
    </span>
  );
};

/** Chip on review-needed rows: detected own-account transfer. Clicking
 *  "Review & confirm" opens a dialog showing BOTH legs (with status and how
 *  the other side is recorded) plus any other possible matches - nothing
 *  executes without explicit approval. */
export const TransferPairChip = ({ tx, currencySymbol: symbolProp = '₦', onDone, compact = false }) => {
  const currencySymbol = txSymbol(tx, symbolProp);
  const [busy, setBusy] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const pair = tx.transfer_pair;
  if (!pair || pair.confirmed) return null;
  const otherLabel = (() => {
    const bank = pair.other_bank_name || '';
    const acct = pair.other_account_name || '';
    if (!acct || acct === bank) return bank;
    return acct.toLowerCase().includes(bank.toLowerCase()) ? acct : `${bank} · ${acct}`;
  })();

  const dismiss = async () => {
    setBusy('dismiss');
    try {
      const r = await axios.post(`${API}/bank-feeds/transfer-pairs/dismiss`, { tx_id: tx.id });
      toast.success(r.data?.message || 'Done');
      onDone?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Action failed');
    } finally { setBusy(null); }
  };

  return (
    <>
      <div
        className={compact
          ? 'flex flex-col items-start gap-1 w-full max-w-full px-2 py-1.5 rounded-md bg-indigo-50 border border-indigo-200 text-[11px] text-indigo-800'
          : 'inline-flex flex-wrap items-center gap-2 max-w-full px-2 py-1 rounded-md bg-indigo-50 border border-indigo-200 text-[11px] text-indigo-800'}
        data-testid={`transfer-pair-chip-${tx.id}`}
      >
        <span className="flex items-start gap-1.5 min-w-0 max-w-full">
          <ArrowLeftRight className="w-3 h-3 shrink-0 mt-0.5" />
          <span className="leading-tight min-w-0">
            <span className="block font-medium">Transfer between your accounts</span>
            {pair.other_bank_name && (
              <span className="block text-[10px] text-indigo-500 truncate" title={`${currencySymbol}${Number(pair.other_amount || 0).toLocaleString()} on ${otherLabel} (${pair.other_date})`}>
                ↔ {currencySymbol}{Number(pair.other_amount || 0).toLocaleString()} on {otherLabel} ({pair.other_date})
              </span>
            )}
          </span>
        </span>
        <span className="flex items-center gap-2 shrink-0">
          <button
            type="button"
            onClick={() => setDialogOpen(true)}
            disabled={!!busy}
            className="px-1.5 py-0.5 rounded bg-indigo-600 text-white font-semibold hover:bg-indigo-700 disabled:opacity-50 whitespace-nowrap"
            data-testid={`transfer-pair-confirm-${tx.id}`}
          >
            Review & confirm
          </button>
          <button
            type="button"
            onClick={dismiss}
            disabled={!!busy}
            className="text-indigo-500 hover:text-indigo-700 underline whitespace-nowrap"
            data-testid={`transfer-pair-dismiss-${tx.id}`}
          >
            Not a transfer
          </button>
        </span>
      </div>
      <TransferPairConfirmDialog
        tx={tx}
        currencySymbol={currencySymbol}
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        onDone={onDone}
      />
    </>
  );
};

/** Oct 2026 · the AI flagged this row as a transfer between own accounts but no other leg was
 *  matched yet. Opens the SAME review dialog (candidate list) - replaces the old one-sided
 *  "Inter-Bank Transfer → Confirm" strip, which could post a transfer nobody verified. */
export const TransferFindOtherSideChip = ({ tx, currencySymbol: symbolProp = '₦', onDone }) => {
  const currencySymbol = txSymbol(tx, symbolProp);
  const [dialogOpen, setDialogOpen] = useState(false);
  if (tx.status === 'completed' || tx.transfer_pair) return null;
  const looksLikeTransfer = !!tx.suggested_destination_bank_account_id
    || String(tx.classification || '').toLowerCase() === 'internal_transfer';
  if (!looksLikeTransfer) return null;
  return (
    <>
      <div className="inline-flex items-center gap-2 px-2 py-1 rounded-md bg-sky-50 border border-sky-200 text-[11px] text-sky-800" data-testid={`transfer-find-chip-${tx.id}`}>
        <ArrowLeftRight className="w-3 h-3 shrink-0" />
        <span className="leading-tight">Looks like a transfer between your accounts<span className="block text-[10px] text-sky-600">Other side not matched yet</span></span>
        <button
          type="button"
          onClick={() => setDialogOpen(true)}
          className="px-1.5 py-0.5 rounded bg-sky-700 text-white font-semibold hover:bg-sky-800 whitespace-nowrap"
          data-testid={`transfer-find-other-side-${tx.id}`}
        >
          Find the other side
        </button>
      </div>
      <TransferPairConfirmDialog tx={tx} currencySymbol={currencySymbol} open={dialogOpen} onOpenChange={setDialogOpen} onDone={onDone} />
    </>
  );
};

/** Amber badge when the running-balance chain broke around this row at extraction time */
export const ExtractionWarningBadge = ({ tx }) => {
  if (!tx.extraction_warning) return null;
  return (
    <span
      className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-semibold bg-amber-50 text-amber-700 border border-amber-300 leading-none"
      title="The statement's running balance didn't add up around this row: the description or amount may have been extracted imperfectly. Double-check against your statement."
      data-testid={`extraction-warning-${tx.id}`}
    >
      <AlertTriangle className="w-2.5 h-2.5" /> Check extraction
    </span>
  );
};

/** Informational chip: fee baked into the amount, will be split on posting */
export const FeeSuggestionChip = ({ tx, currencySymbol: symbolProp = '₦' }) => {
  const currencySymbol = txSymbol(tx, symbolProp);
  const fs = tx.fee_suggestion;
  if (!fs || tx.fee_split) return null;
  // Oct 2026 · a kobo-for-kobo pair proves nothing was deducted - never advertise a fee split
  if (tx.transfer_pair && Math.abs(Number(tx.transfer_pair.fee_diff || 0)) < 0.01) return null;
  return (
    <span
      className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] bg-sky-50 text-sky-700 border border-sky-200 leading-none"
      title="This bank adds its transfer fee into the transaction value. The journal will be split so the fee goes to Bank Charges: the bank line stays at the statement amount for audit."
      data-testid={`fee-suggestion-${tx.id}`}
    >
      <Scissors className="w-2.5 h-2.5" />
      Includes {currencySymbol}{Number(fs.fee).toLocaleString()} transfer fee
    </span>
  );
};

/** Badge + revert on completed rows where a fee was auto-split to Bank Charges */
export const FeeSplitBadge = ({ tx, currencySymbol: symbolProp = '₦', onDone }) => {
  const currencySymbol = txSymbol(tx, symbolProp);
  const [busy, setBusy] = useState(false);
  const fs = tx.fee_split;
  if (!fs || fs.reverted) return null;

  const revert = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/bank-feeds/fee-split/revert`, { tx_id: tx.id });
      toast.success(r.data?.message || 'Fee split reversed');
      onDone?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not revert');
    } finally { setBusy(false); }
  };

  return (
    <span
      className="inline-flex items-center gap-1.5 px-1.5 py-0.5 rounded text-[9px] font-semibold bg-sky-50 text-sky-700 border border-sky-200 leading-none"
      title={`AkountFly detected this bank bakes its transfer fee into the amount. ${currencySymbol}${Number(fs.fee).toLocaleString()} was moved to Bank Charges (journal-level split; the bank line still matches your statement). Click revert to undo.`}
      data-testid={`fee-split-badge-${tx.id}`}
    >
      <Scissors className="w-2.5 h-2.5" />
      {currencySymbol}{Number(fs.fee).toLocaleString()} fee → Bank Charges
      <button type="button" onClick={revert} disabled={busy} className="underline text-sky-500 hover:text-sky-700" data-testid={`fee-split-revert-${tx.id}`}>
        {busy ? '…' : 'revert'}
      </button>
    </span>
  );
};
